import React, { createContext, useContext } from "react";
import { DEFAULT_DICTIONARY_TERMS } from "../../../constants/marketDictionaryData";
import {
    ModernTagBadge,
    ModernTagDot
} from "./dictionaryStyles";

export const getMatchingSnippet = (term, query) => {
    if (!query || !query.trim()) return null;
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return null;

    const sections = [
        { label: 'Key Rule', text: term.ruleOrFormula },
        { label: 'Short Def', text: term.shortDef },
        { label: 'Mechanics', text: term.fullDescription },
        { label: 'Strategy', text: term.application },
        { label: 'Tags', text: Array.isArray(term.tags) ? term.tags.join(', ') : '' },
        { label: 'Diagram', text: term.diagram }
    ];

    for (const token of tokens) {
        for (const sec of sections) {
            if (!sec.text) continue;
            const lower = sec.text.toLowerCase();
            const index = lower.indexOf(token);
            if (index !== -1) {
                const start = Math.max(0, index - 35);
                const end = Math.min(sec.text.length, index + token.length + 55);
                let snippet = sec.text.substring(start, end).replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ');
                if (start > 0) snippet = '...' + snippet;
                if (end < sec.text.length) snippet = snippet + '...';
                return {
                    label: sec.label,
                    snippet
                };
            }
        }
    }
    return null;
};

export const HighlightText = ({ text, query }) => {
    if (!query || !query.trim() || !text || typeof text !== 'string') return <>{text}</>;
    const words = query.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length === 0) return <>{text}</>;

    const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <mark
                        key={i}
                        style={{
                            background: 'rgba(76, 206, 172, 0.35)',
                            color: '#80cbc4',
                            borderRadius: '3px',
                            padding: '1px 3px',
                            fontWeight: '600'
                        }}
                    >
                        {part}
                    </mark>
                ) : (
                    part
                )
            )}
        </>
    );
};

export const renderBadgeSpan = (label, type, key) => {
    let bg = 'rgba(148, 163, 184, 0.12)';
    let color = '#94a3b8';
    let border = 'rgba(148, 163, 184, 0.28)';

    if (type === 'required') {
        bg = 'rgba(16, 185, 129, 0.14)';
        color = '#34d399';
        border = 'rgba(16, 185, 129, 0.35)';
    } else if (type === 'chartable') {
        bg = 'rgba(56, 189, 248, 0.14)';
        color = '#38bdf8';
        border = 'rgba(56, 189, 248, 0.35)';
    } else if (type === 'non-chartable') {
        bg = 'rgba(245, 158, 11, 0.14)';
        color = '#fbbf24';
        border = 'rgba(245, 158, 11, 0.35)';
    }

    return (
        <span
            key={key}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                padding: '2px 8px',
                borderRadius: '5px',
                background: bg,
                color: color,
                border: `1px solid ${border}`,
                marginLeft: '6px',
                verticalAlign: 'middle',
                textTransform: 'uppercase',
                lineHeight: 1.3
            }}
        >
            {label}
        </span>
    );
};

export const getModernTagStyle = (tagText) => {
    const lower = (tagText || "").toLowerCase().trim();
    if (lower.includes('timeframe')) {
        return {
            bg: 'rgba(56, 189, 248, 0.12)',
            color: '#38bdf8',
            border: 'rgba(56, 189, 248, 0.38)',
            glow: 'rgba(56, 189, 248, 0.22)',
            dot: '#38bdf8'
        };
    }
    if (lower.includes('structure')) {
        return {
            bg: 'rgba(52, 211, 153, 0.12)',
            color: '#34d399',
            border: 'rgba(52, 211, 153, 0.38)',
            glow: 'rgba(52, 211, 153, 0.22)',
            dot: '#34d399'
        };
    }
    if (lower.includes('trade plan') || lower.includes('playbook')) {
        return {
            bg: 'rgba(168, 85, 247, 0.14)',
            color: '#c084fc',
            border: 'rgba(168, 85, 247, 0.38)',
            glow: 'rgba(168, 85, 247, 0.22)',
            dot: '#c084fc'
        };
    }
    if (lower.includes('metric') || lower.includes('price quality')) {
        return {
            bg: 'rgba(251, 191, 36, 0.12)',
            color: '#fbbf24',
            border: 'rgba(251, 191, 36, 0.38)',
            glow: 'rgba(251, 191, 36, 0.22)',
            dot: '#fbbf24'
        };
    }
    if (lower.includes('volatilit')) {
        return {
            bg: 'rgba(232, 121, 249, 0.13)',
            color: '#e879f9',
            border: 'rgba(232, 121, 249, 0.38)',
            glow: 'rgba(232, 121, 249, 0.22)',
            dot: '#e879f9'
        };
    }
    if (lower.includes('level') || lower.includes('zone')) {
        return {
            bg: 'rgba(250, 204, 21, 0.12)',
            color: '#facc15',
            border: 'rgba(250, 204, 21, 0.38)',
            glow: 'rgba(250, 204, 21, 0.22)',
            dot: '#facc15'
        };
    }
    if (lower.includes('supply') || lower.includes('demand')) {
        return {
            bg: 'rgba(251, 113, 133, 0.12)',
            color: '#fb7185',
            border: 'rgba(251, 113, 133, 0.38)',
            glow: 'rgba(251, 113, 133, 0.22)',
            dot: '#fb7185'
        };
    }
    if (lower.includes('candle') || lower.includes('pattern')) {
        return {
            bg: 'rgba(45, 212, 191, 0.12)',
            color: '#2dd4bf',
            border: 'rgba(45, 212, 191, 0.38)',
            glow: 'rgba(45, 212, 191, 0.22)',
            dot: '#2dd4bf'
        };
    }
    if (lower.includes('cycle')) {
        return {
            bg: 'rgba(96, 165, 250, 0.12)',
            color: '#60a5fa',
            border: 'rgba(96, 165, 250, 0.38)',
            glow: 'rgba(96, 165, 250, 0.22)',
            dot: '#60a5fa'
        };
    }
    if (lower.includes('liquid') || lower.includes('order book') || lower.includes('flow')) {
        return {
            bg: 'rgba(248, 113, 113, 0.12)',
            color: '#f87171',
            border: 'rgba(248, 113, 113, 0.38)',
            glow: 'rgba(248, 113, 113, 0.22)',
            dot: '#f87171'
        };
    }
    if (lower.includes('average') || lower.includes('moving')) {
        return {
            bg: 'rgba(129, 140, 248, 0.12)',
            color: '#818cf8',
            border: 'rgba(129, 140, 248, 0.38)',
            glow: 'rgba(129, 140, 248, 0.22)',
            dot: '#818cf8'
        };
    }
    if (lower.includes('momentum') || lower.includes('volume')) {
        return {
            bg: 'rgba(163, 230, 53, 0.12)',
            color: '#a3e635',
            border: 'rgba(163, 230, 53, 0.38)',
            glow: 'rgba(163, 230, 53, 0.22)',
            dot: '#a3e635'
        };
    }
    if (lower.includes('management')) {
        return {
            bg: 'rgba(34, 211, 238, 0.12)',
            color: '#22d3ee',
            border: 'rgba(34, 211, 238, 0.38)',
            glow: 'rgba(34, 211, 238, 0.22)',
            dot: '#22d3ee'
        };
    }
    if (lower.includes('all module')) {
        return {
            bg: 'linear-gradient(135deg, rgba(168, 85, 247, 0.18), rgba(236, 72, 153, 0.18))',
            color: '#e879f9',
            border: 'rgba(217, 70, 239, 0.45)',
            glow: 'rgba(217, 70, 239, 0.28)',
            dot: '#ec4899'
        };
    }

    const palette = [
        { bg: 'rgba(56, 189, 248, 0.12)', color: '#38bdf8', border: 'rgba(56, 189, 248, 0.35)', dot: '#38bdf8' },
        { bg: 'rgba(52, 211, 153, 0.12)', color: '#34d399', border: 'rgba(52, 211, 153, 0.35)', dot: '#34d399' },
        { bg: 'rgba(168, 85, 247, 0.12)', color: '#c084fc', border: 'rgba(168, 85, 247, 0.35)', dot: '#c084fc' },
        { bg: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', border: 'rgba(251, 191, 36, 0.35)', dot: '#fbbf24' },
        { bg: 'rgba(251, 113, 133, 0.12)', color: '#fb7185', border: 'rgba(251, 113, 133, 0.35)', dot: '#fb7185' },
        { bg: 'rgba(45, 212, 191, 0.12)', color: '#2dd4bf', border: 'rgba(45, 212, 191, 0.35)', dot: '#2dd4bf' },
        { bg: 'rgba(96, 165, 250, 0.12)', color: '#60a5fa', border: 'rgba(96, 165, 250, 0.35)', dot: '#60a5fa' },
        { bg: 'rgba(232, 121, 249, 0.12)', color: '#e879f9', border: 'rgba(232, 121, 249, 0.35)', dot: '#e879f9' },
        { bg: 'rgba(163, 230, 53, 0.12)', color: '#a3e635', border: 'rgba(163, 230, 53, 0.35)', dot: '#a3e635' },
        { bg: 'rgba(129, 140, 248, 0.12)', color: '#818cf8', border: 'rgba(129, 140, 248, 0.35)', dot: '#818cf8' }
    ];
    let hash = 0;
    for (let i = 0; i < tagText.length; i++) {
        hash = tagText.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % palette.length;
    return palette[idx];
};

export const DictionaryNavContext = createContext({
    navigateToDoc: () => {},
    navigateToTag: () => {},
    terms: []
});

export const resolveTagToTermId = (tagText, availableTerms = DEFAULT_DICTIONARY_TERMS) => {
    if (!tagText || typeof tagText !== 'string') return null;
    const clean = tagText.trim();
    const lower = clean.toLowerCase();

    const termsList = Array.isArray(availableTerms) && availableTerms.length > 0 ? availableTerms : DEFAULT_DICTIONARY_TERMS;

    // 1. Direct match on ID
    const byId = termsList.find(t => t.id && t.id.toLowerCase() === lower);
    if (byId) return byId.id;

    // 2. Direct match on Term Name
    const byTerm = termsList.find(t => t.term && t.term.toLowerCase() === lower);
    if (byTerm) return byTerm.id;

    // 3. Normalized alias mappings
    if (lower.includes('pre-trade') || lower.includes('pre trade') || lower.includes('pretrade')) {
        return 'pre_trade_plan';
    }
    if (lower.includes('concise') || lower.includes('trade plan concise') || lower.includes('trade plan — concise') || lower.includes('trade plan - concise')) {
        return 'trade_plan_concise';
    }
    if (lower === 'trade plan' || lower === 'playbook' || lower.includes('trade plan') || lower.includes('trade plan detailed') || lower.includes('trade plan - detailed')) {
        return 'trade_plan';
    }
    if (lower.includes('trade management') || lower === 'management') {
        return 'trade_management';
    }
    if (lower.includes('timeframe')) {
        return 'timeframes';
    }
    if (lower.includes('cycle')) {
        return 'market_cycles';
    }
    if (lower.includes('price quality')) {
        return 'price_quality';
    }
    if (lower.includes('structure - 2') || lower.includes('structure 2') || lower.includes('secondary structure') || lower.includes('candidate structure')) {
        return 'market_structure_2';
    }
    if (lower.includes('structure')) {
        return 'market_structure';
    }
    if (lower.includes('levels quality') || lower.includes('level quality')) {
        return 'levels_quality';
    }
    if (lower.includes('key level')) {
        return 'key_levels';
    }
    if (lower.includes('supply') || lower.includes('demand')) {
        return 'supply_demand_zone';
    }
    if (lower.includes('candle') || lower.includes('pattern')) {
        return 'patterns_candles_chart';
    }
    if (lower.includes('order book')) {
        return 'order_book';
    }
    if (lower.includes('who moves') || lower.includes('market flow') || lower === 'flow' || lower === 'flows') {
        return 'market_flows';
    }
    if (lower.includes('liquid')) {
        return 'liquidity';
    }
    if (lower.includes('volatility grid') || lower.includes('grid')) {
        return 'volatility_grid';
    }
    if (
        lower.includes('moving average') ||
        lower.includes('average') ||
        lower.includes('price-derived') ||
        lower.includes('price derived') ||
        lower.includes('momentum') ||
        lower === 'volatility'
    ) {
        return 'price_derived_metrics';
    }
    if (lower.includes('volatilit')) {
        return 'volatility_grid';
    }
    if (lower.includes('internal') || lower.includes('contextual')) {
        return 'contextual_filters';
    }

    // 4. Match against term tags
    const byTag = termsList.find(t =>
        Array.isArray(t.tags) && t.tags.some(tag => tag && tag.toLowerCase() === lower)
    );
    if (byTag) return byTag.id;

    return null;
};

export const ClickableModernTag = ({ tagText }) => {
    const nav = useContext(DictionaryNavContext);
    const targetDocId = resolveTagToTermId(tagText, nav?.terms);
    const style = getModernTagStyle(tagText);
    const isClickable = Boolean(targetDocId);

    const handleClick = (e) => {
        if (isClickable && nav?.navigateToDoc && targetDocId) {
            e.stopPropagation();
            e.preventDefault();
            nav.navigateToDoc(targetDocId, { targetSection: 'tag-target', tagText });
        }
    };

    return (
        <ModernTagBadge
            onClick={handleClick}
            $isClickable={isClickable}
            style={{
                background: style.bg,
                color: style.color,
                border: `1px solid ${style.border}`,
                boxShadow: style.glow ? `0 2px 7px ${style.glow}` : '0 1px 3px rgba(0, 0, 0, 0.18)'
            }}
            title={isClickable ? `Go to document: ${tagText} (Click to open)` : `Module: ${tagText}`}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={isClickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(e);
                }
            } : undefined}
        >
            <ModernTagDot
                style={{
                    background: style.dot || style.color,
                    boxShadow: `0 0 5px ${style.dot || style.color}`
                }}
            />
            <span>{tagText}</span>
            {isClickable && (
                <span
                    style={{
                        fontSize: '0.62rem',
                        opacity: 0.65,
                        marginLeft: '1px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        lineHeight: 1
                    }}
                    aria-hidden="true"
                >
                    ↗
                </span>
            )}
        </ModernTagBadge>
    );
};

export const renderModernTag = (tagText, key) => {
    return <ClickableModernTag key={key} tagText={tagText} />;
};

export const renderSourceBadge = (label, key) => {
    return (
        <span
            key={key}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '0.72rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
                padding: '2px 8px',
                borderRadius: '5px',
                background: 'rgba(99, 102, 241, 0.12)',
                color: '#818cf8',
                border: '1px solid rgba(99, 102, 241, 0.28)',
                marginLeft: '6px',
                verticalAlign: 'middle',
                lineHeight: 1.3
            }}
        >
            {label}
        </span>
    );
};

export const renderConceptBadge = (label, isCurve, key) => {
    return (
        <span
            key={key}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                padding: '2px 8px',
                borderRadius: '4px',
                background: isCurve ? 'rgba(56, 189, 248, 0.12)' : 'rgba(168, 85, 247, 0.12)',
                color: isCurve ? '#38bdf8' : '#c084fc',
                border: isCurve ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(168, 85, 247, 0.3)',
                margin: '0 3px',
                verticalAlign: 'middle',
                lineHeight: 1.3
            }}
        >
            {label}
        </span>
    );
};

export const renderInlineMarkdown = (text, query) => {
    if (!text) return null;
    if (typeof text !== 'string') return text;

    if (text.includes('\n')) {
        const lines = text.split('\n');
        return lines.map((line, lIdx) => {
            const trimmed = line.trim();
            if (!trimmed) {
                return <div key={lIdx} style={{ height: '6px' }} />;
            }
            if (/^\s{2,}[-*•]\s+/.test(line)) {
                const subContent = line.replace(/^\s{2,}[-*•]\s+/, '');
                return (
                    <div
                        key={lIdx}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            marginLeft: '20px',
                            marginTop: '3px',
                            marginBottom: '3px',
                            fontSize: '0.92em',
                            color: '#cbd5e1'
                        }}
                    >
                        <span style={{ color: '#38bdf8', fontWeight: 700, lineHeight: '1.4', flexShrink: 0 }}>›</span>
                        <div style={{ flex: 1 }}>{renderInlineMarkdown(subContent, query)}</div>
                    </div>
                );
            }
            if (/^[-*•]\s+/.test(trimmed)) {
                const bulletContent = trimmed.replace(/^[-*•]\s+/, '');
                return (
                    <div
                        key={lIdx}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            marginTop: lIdx === 0 ? 0 : '8px',
                            marginBottom: '2px',
                            fontWeight: 500
                        }}
                    >
                        <span style={{ color: '#10b981', lineHeight: '1.4', flexShrink: 0 }}>•</span>
                        <div style={{ flex: 1 }}>{renderInlineMarkdown(bulletContent, query)}</div>
                    </div>
                );
            }
            return (
                <div key={lIdx} style={{ marginTop: lIdx === 0 ? 0 : '4px' }}>
                    {renderInlineMarkdown(line, query)}
                </div>
            );
        });
    }

    if (/<br\s*\/?>/i.test(text)) {
        const brParts = text.split(/<br\s*\/?>/gi);
        return brParts.map((sub, i) => (
            <React.Fragment key={i}>
                {i > 0 && <br />}
                {renderInlineMarkdown(sub, query)}
            </React.Fragment>
        ));
    }

    const parseTextWithBadges = (str, baseKey) => {
        if (!str) return null;
        const badgeRegex = /(\((?:Required|Optional|Chartable|Non\s*-\s*Chartable|Execution Setup|Valid Origin|Protected Floor|Protected Ceiling|Flip Zone|Area of Interest|blue:[^)]+)\)|\[(?:Required|Optional|Chartable|Non\s*-\s*Chartable|Execution Setup|Valid Origin|Protected Floor|Protected Ceiling|Flip Zone|Area of Interest|blue:[^\]]+)\]|\((?:[^)]*(?:→|->)[^)]*)\)|\[(?:[^\]]*(?:→|->)[^\]]*)\]|\((?:Market Internals\s*:[^)]+)\)|\[(?:Market Internals\s*:[^\]]+)\]|\((?:(?:MN|W1|D1|H4|H1|M30|M15|M5|M1)\s*[/\u2192]\s*)+(?:MN|W1|D1|H4|H1|M30|M15|M5|M1)\)|\bPrice\s+Curve\b|\bPrice\s+Candles?\b|\[(?:Price\s+Curve|Price\s+Candles?|Curve|Candles?)\]|\[[^\]\n]+\](?!\())/gi;
        const parts = str.split(badgeRegex);
        if (parts.length === 1) {
            return <HighlightText key={baseKey} text={str} query={query} />;
        }
        return parts.map((part, pIdx) => {
            if (!part) return null;
            const pKey = `${baseKey}-${pIdx}`;
            if (/^\[?(?:Price\s+Curve|Curve)\]?$/i.test(part.trim())) {
                const cleanLabel = part.trim().replace(/^\[|\]$/g, '');
                return renderConceptBadge(cleanLabel, true, pKey);
            }
            if (/^\[?(?:Price\s+Candles?|Candles?)\]?$/i.test(part.trim())) {
                const cleanLabel = part.trim().replace(/^\[|\]$/g, '');
                return renderConceptBadge(cleanLabel, false, pKey);
            }
            if (/^\(?(?:Required)\)?$/i.test(part) || /^\[(?:Required)\]$/i.test(part)) {
                return renderBadgeSpan('REQUIRED', 'required', pKey);
            }
            if (/^\(?(?:Optional)\)?$/i.test(part) || /^\[(?:Optional)\]$/i.test(part)) {
                return renderBadgeSpan('OPTIONAL', 'optional', pKey);
            }
            if (/^\(?(?:Chartable)\)?$/i.test(part) || /^\[(?:Chartable)\]$/i.test(part)) {
                return renderBadgeSpan('CHARTABLE', 'chartable', pKey);
            }
            if (/^\[blue:(.*)\]$/i.test(part) || /^\(blue:(.*)\)$/i.test(part)) {
                const m = part.match(/^[[(]blue:(.*?)[\])]$/i);
                return renderBadgeSpan(m ? m[1].trim() : part, 'chartable', pKey);
            }
            if (/^\(?(?:Execution Setup|Valid Origin|Protected Floor|Protected Ceiling|Flip Zone|Area of Interest)\)?$/i.test(part) || /^\[(?:Execution Setup|Valid Origin|Protected Floor|Protected Ceiling|Flip Zone|Area of Interest)\]$/i.test(part)) {
                const clean = part.replace(/^[[()\]]/g, '').trim();
                return renderBadgeSpan(clean, 'chartable', pKey);
            }
            if (/^\(?(?:Non\s*-\s*Chartable)\)?$/i.test(part) || /^\[(?:Non\s*-\s*Chartable)\]$/i.test(part)) {
                return renderBadgeSpan('NON-CHARTABLE', 'non-chartable', pKey);
            }
            if (/^\(?(?:Market Internals\s*:\s*([^)]+))\)?$/i.test(part) || /^\[(?:Market Internals\s*:\s*([^\]]+))\]$/i.test(part)) {
                const match = part.match(/(?:Market Internals\s*:\s*([^\])]+))/i);
                const label = match ? match[1].replace(/->/g, '→').trim() : part;
                return renderSourceBadge(label, pKey);
            }
            if (/^\([^)]*(?:→|->)[^)]*\)$/.test(part) || /^\[[^\]]*(?:→|->)[^\]]*\]$/.test(part) || /^\((?:(?:MN|W1|D1|H4|H1|M30|M15|M5|M1)\s*[/\u2192]\s*)+(?:MN|W1|D1|H4|H1|M30|M15|M5|M1)\)$/.test(part)) {
                const inner = part.slice(1, -1).replace(/^Market Internals:\s*/i, '').replace(/->/g, '→').trim();
                return renderSourceBadge(inner, pKey);
            }
            if (/^\[([ xX])?\]$/.test(part.trim())) {
                const isChecked = /x/i.test(part);
                return (
                    <span
                        key={pKey}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '15px',
                            height: '15px',
                            borderRadius: '3.5px',
                            border: isChecked ? '1px solid #10b981' : '1px solid rgba(56, 189, 248, 0.45)',
                            background: isChecked ? 'rgba(16, 185, 129, 0.2)' : 'rgba(15, 23, 42, 0.6)',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                            flexShrink: 0
                        }}
                    >
                        {isChecked ? <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 800, lineHeight: 1 }}>✓</span> : null}
                    </span>
                );
            }
            if (/^\[[^\]\n]+\]$/.test(part.trim())) {
                const innerTag = part.trim().slice(1, -1).trim();
                if (innerTag) {
                    return renderModernTag(innerTag, pKey);
                }
            }
            return <HighlightText key={pKey} text={part} query={query} />;
        });
    };

    const tokens = text.split(/(<u\b[^>]*>[\s\S]*?<\/u>|\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*|==[^=]+==|`[^`]+`)/gi);
    return tokens.map((tok, idx) => {
        if (/^<u\b[^>]*>([\s\S]*?)<\/u>$/i.test(tok)) {
            const uMatch = tok.match(/^<u\b[^>]*>([\s\S]*?)<\/u>$/i);
            const uInner = uMatch ? uMatch[1] : tok;
            return (
                <span
                    key={idx}
                    style={{
                        textDecoration: 'underline',
                        textUnderlineOffset: '3.5px',
                        textDecorationColor: '#38bdf8',
                        textDecorationThickness: '1.5px'
                    }}
                >
                    {parseTextWithBadges(uInner, `u-${idx}`)}
                </span>
            );
        } else if (tok.startsWith('***') && tok.endsWith('***') && tok.length > 6) {
            const biInner = tok.slice(3, -3);
            if (/^\(?(?:Required)\)?$/i.test(biInner) || /^\[(?:Required)\]$/i.test(biInner)) {
                return renderBadgeSpan('REQUIRED', 'required', idx);
            }
            if (/^\(?(?:Optional)\)?$/i.test(biInner) || /^\[(?:Optional)\]$/i.test(biInner)) {
                return renderBadgeSpan('OPTIONAL', 'optional', idx);
            }
            if (/^\(?(?:Chartable)\)?$/i.test(biInner) || /^\[(?:Chartable)\]$/i.test(biInner)) {
                return renderBadgeSpan('CHARTABLE', 'chartable', idx);
            }
            if (/^\(?(?:Non\s*-\s*Chartable)\)?$/i.test(biInner) || /^\[(?:Non\s*-\s*Chartable)\]$/i.test(biInner)) {
                return renderBadgeSpan('NON-CHARTABLE', 'non-chartable', idx);
            }
            return (
                <strong
                    key={idx}
                    className="doc-bold-italic"
                    style={{
                        fontStyle: 'italic',
                        fontWeight: 600,
                        letterSpacing: '0.01em',
                        color: '#e2e8f0'
                    }}
                >
                    {parseTextWithBadges(biInner, `bi-${idx}`)}
                </strong>
            );
        } else if (tok.startsWith('**') && tok.endsWith('**')) {
            const boldInner = tok.slice(2, -2);
            if (/^(?:Price\s+Curve|Curve)$/i.test(boldInner.trim())) {
                return renderConceptBadge(boldInner.trim(), true, idx);
            }
            if (/^(?:Price\s+Candles?|Candles?)$/i.test(boldInner.trim())) {
                return renderConceptBadge(boldInner.trim(), false, idx);
            }
            if (/^\(?(?:Required)\)?$/i.test(boldInner) || /^\[(?:Required)\]$/i.test(boldInner)) {
                return renderBadgeSpan('REQUIRED', 'required', idx);
            }
            if (/^\(?(?:Optional)\)?$/i.test(boldInner) || /^\[(?:Optional)\]$/i.test(boldInner)) {
                return renderBadgeSpan('OPTIONAL', 'optional', idx);
            }
            if (/^\(?(?:Chartable)\)?$/i.test(boldInner) || /^\[(?:Chartable)\]$/i.test(boldInner)) {
                return renderBadgeSpan('CHARTABLE', 'chartable', idx);
            }
            if (/^\(?(?:Non\s*-\s*Chartable)\)?$/i.test(boldInner) || /^\[(?:Non\s*-\s*Chartable)\]$/i.test(boldInner)) {
                return renderBadgeSpan('NON-CHARTABLE', 'non-chartable', idx);
            }
            return (
                <strong key={idx}>
                    {parseTextWithBadges(boldInner, `b-${idx}`)}
                </strong>
            );
        } else if (tok.startsWith('==') && tok.endsWith('==') && tok.length > 4) {
            const blueInner = tok.slice(2, -2);
            return (
                <span
                    key={idx}
                    style={{
                        color: '#38bdf8',
                        fontWeight: 600
                    }}
                >
                    {parseTextWithBadges(blueInner, `blue-${idx}`)}
                </span>
            );
        } else if (tok.startsWith('*') && tok.endsWith('*') && tok.length > 2) {
            const italicInner = tok.slice(1, -1);
            return (
                <em
                    key={idx}
                    style={{
                        fontStyle: 'italic',
                        fontWeight: 400
                    }}
                >
                    {parseTextWithBadges(italicInner, `i-${idx}`)}
                </em>
            );
        } else if (tok.startsWith('`') && tok.endsWith('`')) {
            const rawCode = tok.slice(1, -1);
            let codeInner = rawCode;
            let codeStyle = {
                background: 'rgba(76, 206, 172, 0.08)',
                color: '#6ee7b7',
                padding: '2px 7px',
                borderRadius: '5px',
                fontFamily: 'inherit',
                fontSize: '0.82em',
                fontWeight: 550,
                border: '1px solid rgba(76, 206, 172, 0.2)',
                letterSpacing: '0.01em'
            };

            const lower = rawCode.toLowerCase().trim();

            if (/^(?:purple|violet):/i.test(rawCode)) {
                codeInner = rawCode.replace(/^(?:purple|violet):/i, '').trim();
                codeStyle.background = 'rgba(168, 85, 247, 0.09)';
                codeStyle.color = '#d8b4fe';
                codeStyle.border = '1px solid rgba(168, 85, 247, 0.22)';
            } else if (/^(?:cyan|blue):/i.test(rawCode)) {
                codeInner = rawCode.replace(/^(?:cyan|blue):/i, '').trim();
                codeStyle.background = 'rgba(56, 189, 248, 0.09)';
                codeStyle.color = '#7dd3fc';
                codeStyle.border = '1px solid rgba(56, 189, 248, 0.22)';
            } else if (/^(?:red|rose):/i.test(rawCode)) {
                codeInner = rawCode.replace(/^(?:red|rose):/i, '').trim();
                codeStyle.background = 'rgba(244, 63, 94, 0.09)';
                codeStyle.color = '#fda4af';
                codeStyle.border = '1px solid rgba(244, 63, 94, 0.22)';
            } else if (/^(?:amber|orange|gold):/i.test(rawCode)) {
                codeInner = rawCode.replace(/^(?:amber|orange|gold):/i, '').trim();
                codeStyle.background = 'rgba(245, 158, 11, 0.09)';
                codeStyle.color = '#fcd34d';
                codeStyle.border = '1px solid rgba(245, 158, 11, 0.22)';
            } else if (/^(?:green|emerald):/i.test(rawCode)) {
                codeInner = rawCode.replace(/^(?:green|emerald):/i, '').trim();
                codeStyle.background = 'rgba(16, 185, 129, 0.09)';
                codeStyle.color = '#6ee7b7';
                codeStyle.border = '1px solid rgba(16, 185, 129, 0.22)';
            } else if (/^(?:indigo):/i.test(rawCode)) {
                codeInner = rawCode.replace(/^(?:indigo):/i, '').trim();
                codeStyle.background = 'rgba(99, 102, 241, 0.09)';
                codeStyle.color = '#a5b4fc';
                codeStyle.border = '1px solid rgba(99, 102, 241, 0.22)';
            } else if (lower.includes('dimension') || lower.startsWith('price dim') || lower.startsWith('types')) {
                codeStyle.background = 'rgba(168, 85, 247, 0.09)';
                codeStyle.color = '#d8b4fe';
                codeStyle.border = '1px solid rgba(168, 85, 247, 0.22)';
            } else if (lower.startsWith('trigger') || lower.startsWith('entry')) {
                codeStyle.background = 'rgba(56, 189, 248, 0.09)';
                codeStyle.color = '#7dd3fc';
                codeStyle.border = '1px solid rgba(56, 189, 248, 0.22)';
            } else if (lower.startsWith('sl placement') || lower.startsWith('sl') || lower.includes('invalidation') || lower.startsWith('stop loss') || lower.startsWith('stop-loss')) {
                codeStyle.background = 'rgba(244, 63, 94, 0.09)';
                codeStyle.color = '#fda4af';
                codeStyle.border = '1px solid rgba(244, 63, 94, 0.22)';
            } else if (lower.startsWith('target') || lower.startsWith('tp') || lower.startsWith('rule')) {
                codeStyle.background = 'rgba(16, 185, 129, 0.09)';
                codeStyle.color = '#6ee7b7';
                codeStyle.border = '1px solid rgba(16, 185, 129, 0.22)';
            } else if (lower.includes('trap') || lower.startsWith('warning') || lower.includes('nuance') || lower.startsWith('note')) {
                codeStyle.background = 'rgba(245, 158, 11, 0.09)';
                codeStyle.color = '#fcd34d';
                codeStyle.border = '1px solid rgba(245, 158, 11, 0.22)';
            } else if (lower.startsWith('characteristic') || lower.startsWith('order flow') || lower.startsWith('context') || lower.startsWith('location') || lower.startsWith('use') || lower.startsWith('meaning') || lower.startsWith('wick')) {
                codeStyle.background = 'rgba(99, 102, 241, 0.09)';
                codeStyle.color = '#a5b4fc';
                codeStyle.border = '1px solid rgba(99, 102, 241, 0.22)';
            }

            return (
                <code key={idx} style={codeStyle}>
                    <HighlightText text={codeInner} query={query} />
                </code>
            );
        }
        return parseTextWithBadges(tok, idx);
    });
};

export const findTargetElementForTag = (container, tagText = "", targetDocId = "") => {
    if (!container) return null;
    const lowerTag = (tagText || "").trim().toLowerCase();
    const docId = targetDocId || "";

    // 1. Doc: patterns_candles_chart
    if (docId === 'patterns_candles_chart' || lowerTag.includes('candle') || lowerTag.includes('chart pattern')) {
        if (lowerTag.includes('chart') || lowerTag.includes('classical')) {
            const chartEl = container.querySelector('#doc-section-multi-tf-chart, [data-section="multi-tf-chart"]');
            if (chartEl) return chartEl;
            const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
            for (const h of headings) {
                const text = (h.textContent || '').toLowerCase();
                const parentText = (h.closest('[class*="DocSectionCard"]')?.textContent || '').toLowerCase();
                if (/multi\s*[-–—]?\s*t(?:ime)?f(?:rame)?|where to find/i.test(text) && (text.includes('chart') || parentText.includes('chart pattern') || parentText.includes('06'))) {
                    return h.closest('[class*="DocSectionCard"]') || h;
                }
            }
        } else if (lowerTag.includes('candle')) {
            const candleEl = container.querySelector('#doc-section-multi-tf-candle, [data-section="multi-tf-candle"]');
            if (candleEl) return candleEl;
            const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
            for (const h of headings) {
                const text = (h.textContent || '').toLowerCase();
                const parentText = (h.closest('[class*="DocSectionCard"]')?.textContent || '').toLowerCase();
                if (/multi\s*[-–—]?\s*t(?:ime)?f(?:rame)?|where to find/i.test(text) && (text.includes('candle') || parentText.includes('candlestick') || parentText.includes('03'))) {
                    return h.closest('[class*="DocSectionCard"]') || h;
                }
            }
        }
    }

    // 2. Doc: price_derived_metrics
    if (docId === 'price_derived_metrics' || lowerTag.includes('moving average') || lowerTag.includes('momentum') || lowerTag.includes('volatilit') || lowerTag.includes('volume') || lowerTag.includes('average')) {
        if (lowerTag.includes('moving average') || lowerTag.includes('average') || lowerTag === 'ma' || lowerTag === 'emas') {
            const el = container.querySelector('#doc-section-on-chart-rule, [data-section="on-chart-rule"]');
            if (el) return el;
            const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
            for (const h of headings) {
                const text = (h.textContent || '').toLowerCase();
                if (text.includes('on-chart rule') || text.includes('on chart rule') || (text.includes('trend dimension') && text.includes('ema'))) {
                    return h.closest('[class*="DocSectionCard"]') || h;
                }
            }
        } else if (lowerTag.includes('momentum/volatility/volume') || (lowerTag.includes('momentum') && lowerTag.includes('volume')) || lowerTag.includes('application')) {
            const el = container.querySelector('#doc-section-application, [data-section="application"]');
            if (el) return el;
            const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
            for (const h of headings) {
                const text = (h.textContent || '').toLowerCase();
                if (text.includes('application')) {
                    return h.closest('[class*="DocSectionCard"]') || h;
                }
            }
        } else if (lowerTag.includes('volatilit') && !lowerTag.includes('grid')) {
            const el = container.querySelector('#doc-section-volatility, [data-section="volatility"]');
            if (el) return el;
            const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
            for (const h of headings) {
                const text = (h.textContent || '').toLowerCase();
                if (text.includes('volatility') || text.includes('atr')) {
                    return h.closest('[class*="DocSectionCard"]') || h;
                }
            }
        } else if (lowerTag.includes('momentum')) {
            const el = container.querySelector('#doc-section-momentum, [data-section="momentum"]');
            if (el) return el;
            const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
            for (const h of headings) {
                const text = (h.textContent || '').toLowerCase();
                if (text.includes('momentum')) {
                    return h.closest('[class*="DocSectionCard"]') || h;
                }
            }
        }
    }

    // 3. Doc: volatility_grid
    if (docId === 'volatility_grid' || lowerTag.includes('volatility grid') || (lowerTag.includes('grid') && !lowerTag.includes('order book'))) {
        const el = container.querySelector('#doc-section-volatility-grid-swing, [data-section="volatility-grid-swing"]');
        if (el) return el;
        const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
        for (const h of headings) {
            const text = (h.textContent || '').toLowerCase();
            if (text.includes('cross-asset swing') || text.includes('cross asset swing') || text.includes('multi-hour grid')) {
                return h.closest('[class*="DocSectionCard"]') || h;
            }
        }
    }

    // 4. Doc: key_levels
    if (docId === 'key_levels' || lowerTag.includes('key level')) {
        const el = container.querySelector('#doc-section-chart-based-levels, [data-section="chart-based-levels"]');
        if (el) return el;
        const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"]');
        for (const h of headings) {
            const text = (h.textContent || '').toLowerCase();
            if (text.includes('chart-based level') || text.includes('chart based level')) {
                return h.closest('[class*="DocSectionCard"]') || h;
            }
        }
    }

    // 5. General Multi-TF Analysis check (Market Structure, Levels Quality, Price Quality, Supply/Demand, Market Cycles, Trade Plan Beta)
    const byId = container.querySelector('#doc-section-multi-tf, [data-section="multi-tf"], [id*="multi-tf"]');
    if (byId) return byId;

    const headings = container.querySelectorAll('h1, h2, h3, h4, h5, [class*="DocSectionTitle"], [class*="DocSubSectionTitle"], strong');
    for (const h of headings) {
        if (/multi\s*[-–—]?\s*t(?:ime)?f(?:rame)?/i.test(h.textContent || '')) {
            return h.closest('[class*="DocSectionCard"]') || h.closest('section') || h;
        }
    }

    // 4. Default: No anchor found -> returns null, which defaults to top of page (scrollTop = 0)
    return null;
};

export const findMultiTfElement = findTargetElementForTag;

export const highlightTargetElement = (el) => {
    if (!el) return;
    try {
        el.style.transition = 'box-shadow 0.35s ease, border-color 0.35s ease';
        el.style.borderColor = 'rgba(56, 189, 248, 0.65)';
        el.style.boxShadow = '0 0 0 2px rgba(56, 189, 248, 0.45), 0 6px 24px rgba(56, 189, 248, 0.22)';
        setTimeout(() => {
            if (el) {
                el.style.borderColor = '';
                el.style.boxShadow = '';
            }
        }, 1800);
    } catch (_) {}
};

export const getHistoryItemId = (item) => {
    if (!item) return '';
    return typeof item === 'string' ? item : (item.id || '');
};

export const getHistoryItemScroll = (item) => {
    if (!item || typeof item !== 'object') return 0;
    return typeof item.scrollTop === 'number' ? item.scrollTop : 0;
};
